import { useGetDatasourcesQuery } from 'store/services/helios/dataSources';
import { useGetFunctionsQuery } from 'store/services/helios/functionsService';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { ListItemIcon, MenuItem, styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { ControlButtonsWrapper } from 'components/common/Table/Table.styles';
import { Table } from 'components/common/Table/Table';
import { Button } from 'components/mui/common/Button';
import { heliosFunctionsDataScheme } from 'utils/table/admin/helios/functions';

import type { GetFunctionsRes, FunctionType } from 'types/functions';
import { useModals, useTableQueryControls } from 'utils/hooks';
import { SearchField } from 'components/common/SearchField';
import { AddButton } from 'components/common/Table/actions/AddButton';
import { FilterButton } from 'components/common/Table/actions/FilterButton';
import { SettingsButton } from 'components/common/Table/actions';
import { LoadingContainer } from 'components/common/LoadingContainer';
import { ImportFunctionModal } from './Modals/ImportFunctionModal';
import { AddFunctionModal } from './Modals/AddFunctionModal';
import { EditFunctionModal } from './Modals/EditFunctionModal';
import { ConfirmDeleteFunctionsModal } from './Modals/ConfirmDeleteFunctions';
import { DrawerContentType } from 'components/mui/common/Drawer/DrawerWrapper';
import { setDrawer } from 'store/entities/drawer/actions';
import { TABLE_CELL_TYPE } from 'types/enums';
import { selectFilters } from 'store/entities/drawer/selectors';
import type { GetDatasourcesReq } from 'types';
import type { heliosDataSourcesDataScheme } from 'utils/table/admin/helios/dataSources';
import { initialState } from 'store/entities/drawer';
import { TableHeaderWrapper } from 'components/common/Table/TableHeaderWrapper';

const PageWrapper = styled('div')(() => ({
  width: '100%',
  paddingX: '5',
  marginTop: '10',
}));

const transformFunctionItemData = (item: GetFunctionsRes['items'][number]) => ({
  identifier: item.id,
  id: item.id,
  number: item.number?.toString() as string,
  name: item.name as string,
  column: item.column as string,
  type: item.functionType as string,
  heliosFunctionId: item.heliosFunctionId?.toString() as string,
  isEntryFunction: item.entryFunction,
  parentColumn: item.parentColumn as string,
  isCommon: item.isCommon,
  datasourceName: item.datasourceName,
});

export const DATASOURCES_QUERY_PARAMS: GetDatasourcesReq<typeof heliosDataSourcesDataScheme> = {
  Name: '',
  WsUrlBasic: '',
  WsUrlDomain: '',
  WsUrlGoogle: '',
  WsProfile: '',
  TimeZoneId: '',
  orderBy: 'id',
  orderDesc: true,
  page: 1,
  pageSize: 10000,
  search: '',
};

export const HeliosFunctionsTable = memo(() => {
  const dispatch = useDispatch();
  const filterValues = useSelector(selectFilters('heliosFunctions'));

  const [rowFunctionId, setRowFunctionId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<GetFunctionsRes['items'][number]['id'][]>([]);

  const [modals, openModal, closeModal] = useModals({
    addModal: false,
    editFunctionModal: false,
    importFunctionModal: false,
    confirmDeleteFunctionsModal: false,
  });

  const { t } = useTranslation();

  const { state, setSearch, setSort, setPagination } = useTableQueryControls<typeof heliosFunctionsDataScheme, null>('heliosFunctions', {
    filters: null,
    sort: { by: 'id', order: 'asc' },
    search: '',
    pagination: { page: 1, pageSize: 10 },
  });

  const { data: datasourcesData, isLoading: isDatasourcesLoading } = useGetDatasourcesQuery(DATASOURCES_QUERY_PARAMS);

  const typeFilter = useMemo(() => (filterValues?.find((e) => e.formItemId === 'type')?.value as string) ?? '', [filterValues]);
  const nameFilter = useMemo(() => (filterValues?.find((e) => e.formItemId === 'name')?.value as string) ?? '', [filterValues]);
  const columnFilter = useMemo(() => (filterValues?.find((e) => e.formItemId === 'column')?.value as string) ?? '', [filterValues]);
  const parentColumnFilter = useMemo(() => (filterValues?.find((e) => e.formItemId === 'parentColumn')?.value as string) ?? '', [filterValues]);
  const heliosFunctionIdFilter = useMemo(
    () => (filterValues?.find((e) => e.formItemId === 'heliosFunctionId')?.value as string) ?? '',
    [filterValues]
  );
  const datasourceIdFilter = useMemo(
    () => datasourcesData?.items.find((item) => item.name === (filterValues?.find((e) => e.formItemId === 'datasourceName')?.value as string))?.id,
    [datasourcesData?.items, filterValues]
  );

  const { data, isLoading } = useGetFunctionsQuery({
    Page: state?.pagination.page,
    PageSize: state?.pagination.pageSize,
    OrderBy: state?.sort?.by,
    OrderDesc: state?.sort?.order === 'desc',
    Search: state?.search,
    FunctionType: typeFilter as FunctionType,
    Column: columnFilter,
    Name: nameFilter,
    MasterColumn: parentColumnFilter,
    HeliosFunctionId: heliosFunctionIdFilter ? Number(heliosFunctionIdFilter) : undefined,
    DatasourceId: datasourceIdFilter,
  });

  const handleCloseEditModal = useCallback(() => {
    closeModal('editFunctionModal')();
    setRowFunctionId(null);
  }, [closeModal]);

  const transformedData = useMemo(
    () =>
      data?.items.map((d) => ({
        ...transformFunctionItemData(d),
        type:
          d.functionType === 'Heg'
            ? t('helios.functions.addFunctionModal.fields.functionTypes.Heg')
            : d.functionType === 'HegWorkflow'
              ? t('helios.functions.addFunctionModal.fields.functionTypes.HegWorkflow')
              : d.functionType,
      })),
    [data, t]
  );

  const handleFilterClick = useCallback(() => {
    const { id, type, datasourceName, ...dataSchema } = heliosFunctionsDataScheme;

    const schema = {
      ...dataSchema,
      type: {
        label: 'type',
        type: TABLE_CELL_TYPE.NAME_ARRAY,
        translationKey: t('table.header.type'),
        allowMultiple: false,
        options: ['Heg', 'HegWorkflow'],
      },
      datasourceName: {
        label: 'datasourceName',
        type: TABLE_CELL_TYPE.NAME_ARRAY,
        translationKey: t('table.header.datasourceName'),
        allowMultiple: false,
        options: datasourcesData?.items.map((item) => item.name),
      },
    };

    dispatch(
      setDrawer({
        contentType: DrawerContentType.FILTER_DRAWER,
        isOpen: true,
        dataSchema: schema,
        agendaId: '',
        viewId: '',
        canSave: false,
        entity: 'heliosFunctions',
      })
    );
  }, [t, datasourcesData?.items, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        setDrawer({
          ...initialState,
          entity: 'heliosFunctions',
        })
      );
    };
  }, [dispatch]);

  return (
    <>
      <PageWrapper>
        <LoadingContainer loading={isLoading || isDatasourcesLoading}>
          {transformedData && (
            <Table
              dataSchema={heliosFunctionsDataScheme}
              data={transformedData ?? []}
              onSelectRow={(selected) => setSelectedRows(selected)}
              onEntireRowClick={(row) => {
                setRowFunctionId(row.id);
                openModal('editFunctionModal')();
              }}
              rows={data?.totalCount}
              page={state?.pagination.page ?? 1}
              pageSize={state?.pagination.pageSize ?? 10}
              onPageSizeChange={(pageSize) => {
                setPagination({
                  page: 1,
                  pageSize,
                });
              }}
              onCurrentPageChange={(page) => {
                setPagination({
                  page,
                  pageSize: state?.pagination.pageSize ?? 10,
                });
              }}
              onSort={({ column, direction }) =>
                setSort({
                  by: column,
                  order: direction,
                })
              }
              sort={{
                column: state?.sort?.by ?? 'id',
                direction: state?.sort?.order ?? 'asc',
              }}
              controls={
                <TableHeaderWrapper>
                  <SearchField placeholder={t('common.search.records')} value={state?.search} onChange={(e) => setSearch(e.target.value)} />
                  <ControlButtonsWrapper>
                    <AddButton onClick={openModal('addModal')} />
                    <Button variant="outlined" startIcon={<InsertDriveFileIcon />} onClick={openModal('importFunctionModal')}>
                      {t('table.buttons.importFunctions')}
                    </Button>
                    <FilterButton onClick={handleFilterClick} disabled={isLoading} activeCount={filterValues?.length} />

                    <SettingsButton disabled={selectedRows.length === 0}>
                      <MenuItem onClick={openModal('confirmDeleteFunctionsModal')} disabled={selectedRows?.length === 0}>
                        <ListItemIcon>
                          <Delete fontSize="small" />
                        </ListItemIcon>
                        {t('table.buttons.delete')}
                      </MenuItem>
                    </SettingsButton>
                  </ControlButtonsWrapper>
                </TableHeaderWrapper>
              }
            />
          )}
        </LoadingContainer>
      </PageWrapper>
      <AddFunctionModal open={modals.addModal} onClose={closeModal('addModal')} />
      <ImportFunctionModal open={modals.importFunctionModal} onClose={closeModal('importFunctionModal')} />
      <EditFunctionModal open={modals?.editFunctionModal} functionId={rowFunctionId} onClose={handleCloseEditModal} />
      <ConfirmDeleteFunctionsModal
        ids={selectedRows?.map((id) => id.toString()) ?? []}
        open={modals.confirmDeleteFunctionsModal}
        onClose={closeModal('confirmDeleteFunctionsModal')}
      />
    </>
  );
});
